import {
  BottomSheet,
  type BottomSheetHandle,
} from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { SkeletonGroup } from "@-/dev.improve.haniverse.components.ui.skeleton";
import {
  useCommonMorphemeCompoundCharactersQueryOptions,
  useMorphemeMnemonicUsageCharactersQueryOptions,
} from "@-/dev.improve.haniverse.lib.morpheme.read";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { useMorphemeCorpusFilter } from "@-/dev.improve.haniverse.lib.morpheme.corpus.use";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { type ReactElement, type ReactNode, type Ref, useCallback, useState } from "react";
import { BottomSheetFlatList } from "@-/com.npmjs.gorhom__bottom-sheet";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import {
  MorphemeMnemonicsMeaningCharacterExampleSkeleton,
  MorphemeMnemonicsMeaningCharacterExampleSurface,
} from "#morpheme-mnemonics-meaning-character-example-surface";

/** What the examples sheet lists: a character's common compounds, or the mnemonics using it. */
export type MorphemeMnemonicsMeaningExamplesSheetView =
  | { grapheme: string; graphemeId: number; kind: "compounds" }
  | { grapheme: string; kind: "mnemonic-usages"; mnemonicTerms: readonly string[] };

type PushMnemonicUsages = (character: string, mnemonicTerms: readonly string[]) => void;

function useCorpusCharacters() {
  const { isGraphemeInCorpus } = useMorphemeCorpusFilter();
  // Filtered in `select` rather than in the query so the shared reads stay user-independent: a
  // character outside the browsing corpus is simply not listed here.
  return useCallback(
    (characters: string[]) => characters.filter((character) => isGraphemeInCorpus(character)),
    [isGraphemeInCorpus],
  );
}

function ExamplesList({
  characters,
  emptyText,
  header,
  isPending,
  loadingLabel,
  onPressMnemonicTerms,
  testID,
}: {
  characters: readonly string[];
  emptyText: string;
  header: ReactElement;
  isPending: boolean;
  loadingLabel: string;
  onPressMnemonicTerms: PushMnemonicUsages;
  testID: string;
}) {
  return (
    <BottomSheetFlatList
      data={characters}
      initialNumToRender={6}
      ItemSeparatorComponent={() => <View className="h-3" />}
      keyExtractor={(character) => character}
      ListEmptyComponent={
        isPending ? (
          <SkeletonGroup accessibilityLabel={loadingLabel} className="gap-3">
            {[0, 1, 2].map((key) => (
              <MorphemeMnemonicsMeaningCharacterExampleSkeleton key={key} />
            ))}
          </SkeletonGroup>
        ) : (
          <View className="py-8">
            <Text className="text-[15px] leading-6 text-muted-foreground italic">{emptyText}</Text>
          </View>
        )
      }
      ListHeaderComponent={header}
      maxToRenderPerBatch={6}
      renderItem={({ item }) => (
        <MorphemeMnemonicsMeaningCharacterExampleSurface
          character={item}
          onPressMnemonicTerms={onPressMnemonicTerms}
        />
      )}
      testID={testID}
      windowSize={3}
    />
  );
}

function ExamplesHeader({
  centered = false,
  description,
  eyebrow,
  mnemonicTerms,
  onBack,
  title,
}: {
  centered?: boolean;
  description?: string;
  eyebrow: string;
  mnemonicTerms?: string;
  onBack: (() => void) | undefined;
  title: ReactNode;
}) {
  return (
    <View className="mb-3 gap-1 border-b border-border pb-3">
      {onBack === undefined ? null : (
        <Pressable
          accessibilityLabel="Back"
          accessibilityRole="button"
          className="mb-1 -ml-1 flex-row items-center self-start"
          hitSlop={8}
          onPress={onBack}
          testID="mnemonic-examples-back"
        >
          <Icon className="text-muted-foreground" name="FiChevronLeft" size={18} />
          <Text className="text-[14px] leading-5 text-muted-foreground">Back</Text>
        </Pressable>
      )}
      <Text
        className={`text-[11px] font-bold tracking-widest text-muted-foreground uppercase ${centered ? "text-center" : ""}`}
      >
        {eyebrow}
      </Text>
      <Text
        className={`text-[22px] leading-7 font-bold text-foreground ${centered ? "text-center" : ""}`}
      >
        {title}
      </Text>
      {mnemonicTerms ? (
        <Text className="text-center text-[22px] leading-7 font-normal text-foreground italic underline">
          {mnemonicTerms}
        </Text>
      ) : null}
      {description ? (
        <Text className="text-[14px] leading-5 text-muted-foreground">{description}</Text>
      ) : null}
    </View>
  );
}

function CompoundsExamples({
  grapheme,
  graphemeId,
  onBack,
  onPressMnemonicTerms,
}: {
  grapheme: string;
  graphemeId: number;
  onBack: (() => void) | undefined;
  onPressMnemonicTerms: PushMnemonicUsages;
}) {
  const { hanziScript } = useActorUser();
  const { data: characters = [], isPending } = useQuery({
    ...useCommonMorphemeCompoundCharactersQueryOptions(graphemeId, hanziScript),
    select: useCorpusCharacters(),
  });
  return (
    <ExamplesList
      characters={characters}
      emptyText={`No compounds using ${grapheme} are available yet.`}
      header={
        <ExamplesHeader
          description="Each example pairs the compound's meaning with its authored mnemonic."
          eyebrow={`Compounds using ${grapheme}`}
          onBack={onBack}
          title="See it in larger characters"
        />
      }
      isPending={isPending}
      loadingLabel="Loading compound examples"
      onPressMnemonicTerms={onPressMnemonicTerms}
      testID="mnemonic-compound-examples-list"
    />
  );
}

function MnemonicUsagesExamples({
  grapheme,
  mnemonicTerms,
  onBack,
  onPressMnemonicTerms,
}: {
  grapheme: string;
  mnemonicTerms: readonly string[];
  onBack: (() => void) | undefined;
  onPressMnemonicTerms: PushMnemonicUsages;
}) {
  const { hanziScript } = useActorUser();
  const { data: characters = [], isPending } = useQuery({
    ...useMorphemeMnemonicUsageCharactersQueryOptions(grapheme, hanziScript),
    select: useCorpusCharacters(),
  });
  const mnemonicTermsText = mnemonicTerms.join(", ");
  return (
    <ExamplesList
      characters={characters}
      emptyText={`No mnemonics using ${grapheme} are available yet.`}
      header={
        <ExamplesHeader
          centered
          eyebrow={`${grapheme} as a component`}
          mnemonicTerms={mnemonicTermsText}
          onBack={onBack}
          title="Mnemonics"
        />
      }
      isPending={isPending}
      loadingLabel="Loading mnemonic examples"
      onPressMnemonicTerms={onPressMnemonicTerms}
      testID="mnemonic-usage-examples-list"
    />
  );
}

/**
 * Full-height sheet of example characters, each a surface pairing its glyph, gloss and component
 * mnemonic with its authored mnemonic. Opens on `initialView`; pressing an example's mnemonic
 * switches the sheet to the mnemonics that use that character, and Back returns to the previous
 * list. The view history is dropped with the sheet when it is dismissed.
 */
export function MorphemeMnemonicsMeaningExamplesSheet({
  initialView,
  onDismiss,
  ref,
}: {
  initialView: MorphemeMnemonicsMeaningExamplesSheetView;
  onDismiss: () => void;
  ref?: Ref<BottomSheetHandle>;
}) {
  const [views, setViews] = useState<readonly MorphemeMnemonicsMeaningExamplesSheetView[]>([
    initialView,
  ]);
  const view = views.at(-1) ?? initialView;
  const pushMnemonicUsages = useCallback<PushMnemonicUsages>((character, mnemonicTerms) => {
    setViews((current) => [
      ...current,
      { grapheme: character, kind: "mnemonic-usages", mnemonicTerms },
    ]);
  }, []);
  const onBack =
    views.length > 1
      ? () => {
          setViews((current) => current.slice(0, -1));
        }
      : undefined;
  // Keyed by depth so each view gets a fresh list scrolled to its top.
  return (
    <BottomSheet fullHeight hasScrollableContent onDismiss={onDismiss} ref={ref}>
      {view.kind === "compounds" ? (
        <CompoundsExamples
          grapheme={view.grapheme}
          graphemeId={view.graphemeId}
          key={views.length}
          onBack={onBack}
          onPressMnemonicTerms={pushMnemonicUsages}
        />
      ) : (
        <MnemonicUsagesExamples
          grapheme={view.grapheme}
          key={views.length}
          mnemonicTerms={view.mnemonicTerms}
          onBack={onBack}
          onPressMnemonicTerms={pushMnemonicUsages}
        />
      )}
    </BottomSheet>
  );
}
